import { CreateUserDto } from "../../core/models/dtos/CreateUserDto";
import { UpdateUserDto } from "../../core/models/dtos/UpdateUserDto";
import { useApiClient } from "../clients/ApiClient";
import { mapFromCreateUserDto } from "../models/mappers/mapUser";
import { User } from "../../core/models/entities/User";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";
import { PageQuery, toQueryParam } from "../../utils/models/pageQuery";
import { PagedResponse } from "../models/responses/pagedResponse";
import { VendorUserDto } from "../models/responses/VendorUserDto";
import { PasswordLinkDto } from "../models/responses/PasswordLinkDto";
import { DataError } from "../../core/errors/DataError";
import { adminSwrListOptions } from "./adminSwr";
import { enviroment } from "../../enviroment";
import { isCreateUserMailNotSentError } from "../../presentation/screens/customer-update/models/userStatus";

/** The API's 409 of POST .../welcome-mail: the user has already logged in. */
export const WELCOME_MAIL_ALREADY_LOGGED_IN_CODE = 409;

export interface UserRepository {
  createUser: (dto: CreateUserDto) => Promise<void>;
  /**
   * Deletes a user and drops the row from the cached ["users", orgId] list
   * right away, then refetches that list (same pattern as createUser /
   * updateUser). orgId is optional as a fallback (a caller whose org is not
   * resolved yet): without it every cached users list is patched and the
   * prefix is marked stale instead. When the DELETE fails the cache is left
   * untouched, but the list on screen is marked stale so it reconciles with
   * the server (the row may be gone even though the client saw an error).
   */
  deleteUser: (userId: number, orgId?: number) => Promise<void>;
  /**
   * Updates name/phone of a user. The API (PUT /users) writes email and
   * hasLoggedIn unconditionally, so the full current user is required to
   * round-trip the fields this screen does not edit.
   */
  updateUser: (user: User, orgId: number, dto: UpdateUserDto) => Promise<void>;
  /** Per-user price visibility within an organization. */
  setUserHidePrices: (orgId: number, userId: number, hidePrices: boolean) => Promise<void>;
  /**
   * User deactivation (flag userDeactivateEnabled): deactivates
   * (active=false) or reactivates (active=true) a user - ONE PATCH. On
   * success the cached ["users", orgId] row gets the API's fresh status
   * fields right away (deactivatedUtc / deactivatedBy / reactivatedUtc) and
   * the list is refetched like setUserHidePrices. Every error is rethrown
   * for the toast (409 selfDeactivate / lastActiveUser and 502 on a
   * reactivation = nothing changed server-side, the cache is left as it is).
   */
  setUserActive: (orgId: number, userId: number, active: boolean) => Promise<User>;
  /**
   * Customer onboarding (flag customerOnboardingEnabled): resends the welcome
   * mail (= a new temporary password) to a user who never logged in,
   * optionally with a copy to the org's primary contact. 1 POST. On success
   * the cached ["users", orgId] row gets the API's fresh onboarding fields
   * right away (welcomeMailSentUtc / welcomeMailSendCount /
   * canResendWelcomeMail) and the list is refetched like setUserHidePrices.
   * A 409 (the user has logged in meanwhile) marks the list stale so the
   * button reconciles with the server; every error is rethrown for the toast.
   */
  resendWelcomeMail: (
    orgId: number,
    userId: number,
    ccPrimaryContact: boolean
  ) => Promise<User>;
  /**
   * Admin "Reset code & create link" (flag passwordSetLinkEnabled): ONE
   * POST, answers the one-time link + its expiry. Nothing in the users
   * list changes (no cache write, no refetch); every error is rethrown for
   * the dialog's texts (403 not allowed / 429 quota / 400 no login account).
   */
  createPasswordLink: (
    orgId: number,
    userId: number,
    blockCurrentPassword: boolean
  ) => Promise<PasswordLinkDto>;
  useUsersOfOrganization: (orgId: number, options?: { enabled?: boolean }) => Resource<User[]>;
  /**
   * Admin vendor-user lookup: server-paged/searchable list of vendor users
   * with the vendor they belong to. The server searches across name/email/
   * vendor, so only the search term is forwarded. keepPreviousData keeps the
   * current page visible while the next one loads. Throws through to an error
   * state until the endpoint is live.
   */
  useVendorUsers: (query: PageQuery) => Resource<PagedResponse<VendorUserDto>>;
}

type DeliveryFields = Pick<
  User,
  | "welcomeMailDeliveryStatus"
  | "welcomeMailDeliveryAtUtc"
  | "welcomeMailDeliveryReason"
>;

/** The delivery fields the resend answer carries (present keys only, null kept). */
function deliveryPatch(
  updated: Partial<User> | undefined
): Partial<DeliveryFields> {
  const patch: Partial<DeliveryFields> = {};
  if (!updated) return patch;
  if ("welcomeMailDeliveryStatus" in updated) {
    patch.welcomeMailDeliveryStatus =
      updated.welcomeMailDeliveryStatus ?? null;
  }
  if ("welcomeMailDeliveryAtUtc" in updated) {
    patch.welcomeMailDeliveryAtUtc = updated.welcomeMailDeliveryAtUtc ?? null;
  }
  if ("welcomeMailDeliveryReason" in updated) {
    patch.welcomeMailDeliveryReason =
      updated.welcomeMailDeliveryReason ?? null;
  }
  return patch;
}

export function useUserRepository(): UserRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  async function invalidateQueries(orgId: number) {
    await queryClient.refetchQueries({
      queryKey: ["users", orgId],
      type: "all",
      refetchPage: () => true,
    });
  }

  async function createUser(dto: CreateUserDto): Promise<void> {
    try {
      await apiClient.createUser(mapFromCreateUserDto(dto));
    } catch (error) {
      // Flag userCreateMailStatusEnabled: the API's honest 502
      // welcomeMailNotSent (API flag UserCreateHardeningEnabled) means the
      // user WAS created - refetch the list so the new row shows (ONE GET,
      // only on this error path), then rethrow for the caller's toast. Flag
      // off, or any other error = the cache untouched, exactly as today.
      if (
        enviroment.userCreateMailStatusEnabled &&
        isCreateUserMailNotSentError(error)
      ) {
        invalidateQueries(dto.organizationId).then();
      }
      throw error;
    }
    invalidateQueries(dto.organizationId).then();
  }

  async function deleteUser(userId: number, orgId?: number): Promise<void> {
    const usersKey = orgId === undefined ? ["users"] : ["users", orgId];
    try {
      await apiClient.deleteUser(userId);
    } catch (error) {
      // A failed DELETE (DataError) leaves the cache untouched so the error
      // toast shows the list as the client last saw it - but the server may
      // already have dropped the row (timeout / connection loss after the
      // DELETE landed). Mark the list stale so an active one reconciles with
      // server truth: at most one GET, none when no list is on screen. A
      // failed reconcile keeps the current data (react-query retains data on
      // a refetch error; callers read only data/isLoading).
      queryClient.invalidateQueries({ queryKey: usersKey }).then();
      throw error;
    }
    // Optimistic removal from every cached users list (prefix ["users"]):
    // the row disappears on the next render instead of surviving under the
    // global staleTime/refetchOnMount:false defaults until a full reload.
    // Returning undefined for a list that does not hold the row (or is not
    // an array) skips the write entirely - query-core only calls setData on a
    // defined result - so other orgs' lists and any foreign-shaped key under
    // the prefix keep their data, dataUpdatedAt and staleTime clock.
    queryClient.setQueriesData<User[] | undefined>(
      { queryKey: ["users"] },
      (old) => {
        if (!Array.isArray(old)) return undefined;
        const next = old.filter((u) => u.id !== userId);
        return next.length === old.length ? undefined : next;
      }
    );
    // Server truth afterwards: the same refetch createUser/updateUser use
    // when the org is known; otherwise mark the prefix stale (refetches only
    // active lists, i.e. the one on screen).
    if (orgId !== undefined) {
      invalidateQueries(orgId).then();
    } else {
      queryClient.invalidateQueries({ queryKey: usersKey }).then();
    }
  }

  async function updateUser(
    user: User,
    orgId: number,
    dto: UpdateUserDto
  ): Promise<void> {
    await apiClient.updateUser({
      id: user.id,
      organizationId: user.organizationId ?? orgId,
      // Sent back unchanged: the API overwrites these on every PUT.
      email: user.email,
      hasLoggedIn: user.hasLoggedIn ?? false,
      firstName: dto.firstName ?? user.firstName ?? "",
      lastName: dto.lastName ?? user.lastName ?? "",
      phone: dto.phone ?? user.phone,
    });
    invalidateQueries(orgId).then();
  }

  async function setUserHidePrices(
    orgId: number,
    userId: number,
    hidePrices: boolean
  ): Promise<void> {
    await apiClient.setUserHidePrices(orgId, userId, { hidePrices });
    invalidateQueries(orgId).then();
  }

  async function setUserActive(
    orgId: number,
    userId: number,
    active: boolean
  ): Promise<User> {
    // Every error (409 nothing changed, 502 nothing changed, 403/404) is
    // rethrown as it is for the toast; the cache stays as it was.
    const updated = await apiClient.setUserStatus(orgId, userId, { active });
    // Optimistic patch from the API's own answer (the stamped user): only
    // the status fields, so nothing else in the row moves before the
    // refetch. Returning undefined for a list without the row skips the
    // write (query-core only calls setData on a defined result).
    queryClient.setQueryData<User[] | undefined>(["users", orgId], (old) => {
      if (!Array.isArray(old) || !old.some((u) => u.id === userId)) {
        return undefined;
      }
      return old.map((u) =>
        u.id === userId
          ? {
              ...u,
              deactivatedUtc: updated?.deactivatedUtc ?? null,
              deactivatedBy: updated?.deactivatedBy ?? null,
              reactivatedUtc: updated?.reactivatedUtc ?? u.reactivatedUtc ?? null,
            }
          : u
      );
    });
    invalidateQueries(orgId).then();
    return updated;
  }

  async function resendWelcomeMail(
    orgId: number,
    userId: number,
    ccPrimaryContact: boolean
  ): Promise<User> {
    let updated: User;
    try {
      updated = await apiClient.resendWelcomeMail(orgId, userId, {
        ccPrimaryContact,
      });
    } catch (error) {
      // 409 = the user logged in since the list was loaded (the API changed
      // nothing). The row's canResendWelcomeMail is stale: mark the list so
      // the active one refetches (at most one GET) and the button turns
      // into the "already logged in" state. Other errors leave the cache as
      // it is (a 502 = password reset but no mail: the stamp is unchanged).
      if (
        error instanceof DataError &&
        error.errorStatus?.code === WELCOME_MAIL_ALREADY_LOGGED_IN_CODE
      ) {
        queryClient.invalidateQueries({ queryKey: ["users", orgId] }).then();
      }
      throw error;
    }
    // Optimistic patch from the API's own answer (the stamped user): only
    // the onboarding fields, so nothing else in the row moves before the
    // refetch. Returning undefined for a list without the row skips the
    // write (query-core only calls setData on a defined result).
    queryClient.setQueryData<User[] | undefined>(["users", orgId], (old) => {
      if (!Array.isArray(old) || !old.some((u) => u.id === userId)) {
        return undefined;
      }
      return old.map((u) =>
        u.id === userId
          ? {
              ...u,
              welcomeMailSentUtc:
                updated?.welcomeMailSentUtc ?? u.welcomeMailSentUtc ?? null,
              welcomeMailSendCount:
                updated?.welcomeMailSendCount ??
                (u.welcomeMailSendCount ?? 0) + 1,
              canResendWelcomeMail:
                updated?.canResendWelcomeMail ?? u.canResendWelcomeMail,
              // Delivery fields (API flag WelcomeMailDeliveryStatusEnabled):
              // the answer's value wins whenever the key is present - also
              // null, which is the point: a fresh send clears a previous
              // "notDelivered / Template Render Failure" to "sent" with no
              // reason. Absent (older API) = the row's own values stay.
              ...deliveryPatch(updated),
            }
          : u
      );
    });
    invalidateQueries(orgId).then();
    return updated;
  }

  function createPasswordLink(
    orgId: number,
    userId: number,
    blockCurrentPassword: boolean
  ): Promise<PasswordLinkDto> {
    return apiClient.createPasswordLink(orgId, userId, { blockCurrentPassword });
  }

  function useUsersOfOrganization(orgId: number, options?: { enabled?: boolean }): Resource<User[]> {
    return useQuery(["users", orgId], () =>
      apiClient.getUsersOfOrganization(orgId).then(),
      { enabled: options?.enabled ?? true }
    );
  }

  function useVendorUsers(query: PageQuery): Resource<PagedResponse<VendorUserDto>> {
    return useQuery(
      ["vendor-users", toQueryParam(query)],
      () => apiClient.getVendorUsers(query),
      {
        // Admin surface — reflect the current server state on entry, and keep
        // the previous page visible while the next one loads.
        staleTime: 0,
        keepPreviousData: true,
        // Admin perf FE-3 (flag): paint from cache + one background refetch.
        ...adminSwrListOptions(),
      }
    );
  }

  return {
    createUser,
    deleteUser,
    updateUser,
    setUserHidePrices,
    setUserActive,
    resendWelcomeMail,
    createPasswordLink,
    useUsersOfOrganization,
    useVendorUsers,
  };
}
